import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import get from 'lodash/get';
import { PRODUCT_FILTER_TYPE } from 'app/constants/product';
import buildConfig from 'app/utils/buildConfig';
import ProductPassport from 'app/components/product/ProductPassport';

import api from 'app/api/productApi';

// Components.

// Styles.
import styled from 'styled-components';

const Arrow = styled.div`
  background: ${props => props.theme.colors.neutral2};
  opacity: 0.8;
  line-height: 24px;
  height: 24px;
  text-align: center;
  width: 24px;
  border-radius: 50%;
  cursor: pointer;
  top: 50%;
  margin-top: -12px;
  position: absolute;
  left: ${props => (props.left !== undefined ? props.left : 'auto')};
  right: ${props => (props.right !== undefined ? props.right : 'auto')};
  &:hover {
    opacity: 1;
  }
`;

const HeaderLeft = styled.h4`
  font-weight: 600;
  font-size: 12px;
  position: absolute;
  background: white;
  top: -10px;
  padding: 0 8px 0 0;
  left: 0;
`;

const HeaderRight = styled.a`
  font-weight: normal;
  text-decoration: none;
  font-size: 12px;
  position: absolute;
  background: white;
  top: -10px;
  padding: 0 16px 0 8px;
  right: 0;
  color: ${props => props.theme.colors.neutral5}; !important;

  :hover {
    color: ${props => props.theme.colors.seedlyBlue}; !important;
  }
`;

const Container = styled.div`
  border-top: 1px solid ${props => props.theme.colors.neutral4};
  border-bottom: 1px solid ${props => props.theme.colors.neutral4};
  position: relative;
  padding: 15px 0;
  margin: 15px 0;
`;

const ProductContainer = styled.div`
  white-space: nowrap;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 10px;
  grid-template-columns: repeat(auto-fill, 300px);
  scroll-behavior: smooth;
  position: relative;
`;

const ProductItemLink = styled.div`
  text-decoration: none;
  color: inherit;
  display: inline-block;
  border-radius: 5px;
  border: 1px solid ${props => props.theme.colors.neutral4};
  max-width: 320px;
  width: 100%;
  padding: 8px;
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: !!error };
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI.
      return <div />;
    }

    return this.props.children;
  }
}

const CarouselItem = React.forwardRef(
  ({ product, selected, categorySlug }, scroller) => {
    const ref = useRef(null);

    useEffect(() => {
      if (selected && scroller) {
        try {
          // In case there are browsers that don't support this.
          scroller.current.scrollTo({
            behaviour: 'smooth',
            left: ref.current.offsetLeft,
            top: 0,
          });
        } catch (err) {
          console.log(err);
        }
      }
    }, [selected, scroller]);

    const detailLink = `${buildConfig.apiHost}/reviews/${categorySlug}/${product.slug}`;

    return (
      <ProductItemLink ref={ref}>
        <ProductPassport
          product={product}
          customProductUrl={detailLink}
          variant="widget"
        />
      </ProductItemLink>
    );
  },
);

const Product = () => {
  const router = useRouter();
  const [selected, setSelected] = useState(0);
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);
  const scroller = useRef(null);

  const { product_slug: productSlug, category_slug: categorySlug } =
    router.query || {};

  const handleNext = () =>
    setSelected(prev => Math.min(prev + 1, products.length - 1));

  const handlePrev = () => setSelected(prev => Math.max(prev - 1, 0));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const result = await api.getProducts(
          {
            slugs: productSlug.split(',').map(slug => slug.trim()),
            category_slug: categorySlug,

            sort: {
              by: PRODUCT_FILTER_TYPE.MOST_POPULAR,
              dir: 'asc',
            },
          },
          {},
        );
        setProducts(get(result, 'data.data', []));
      } catch (err) {
        setError(err);
      }
    };
    fetchProducts();
  }, []);

  if (error) {
    return (
      <div>
        Go to <a href="https://www.seedly.sg">Seedly.sg</a>
      </div>
    );
  }

  const hasProducts = products && products.length > 0;

  const allProductsLink = `${buildConfig.apiHost}/reviews/${categorySlug}`;

  const handleClickAllProducts = evt => {
    evt.preventDefault();
    parent.window.open(allProductsLink, '_blank');
  };
  return (
    hasProducts && (
      <Container>
        <Head>
          <meta name="robots" content="noindex,follow" />
        </Head>
        <HeaderLeft>Products Mentioned</HeaderLeft>
        <HeaderRight
          href={allProductsLink}
          rel="noopener noreferrer"
          target="_blank"
          onClick={handleClickAllProducts}
        >
          See All Products
        </HeaderRight>

        <ProductContainer ref={scroller}>
          {products.map((product, i) => (
            <CarouselItem
              key={product.id}
              selected={i === selected}
              product={product}
              ref={scroller}
              categorySlug={categorySlug}
            />
          ))}
        </ProductContainer>
        <Arrow onClick={handlePrev} left={0}>
          ‹
        </Arrow>
        <Arrow onClick={handleNext} right={0}>
          ›
        </Arrow>
      </Container>
    )
  );
};

const View = () => (
  <ErrorBoundary>
    <Product />
  </ErrorBoundary>
);

export default View;
